import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useProgress } from '@react-three/drei';
import { Router, useRoute, useLocation } from 'wouter';
import './styles.css';
import { App } from './App';
import { JozLlmDashboardPage } from './features/joz-llm/JozLlmDashboardPage';
import { AiEconomicsCalculatorPage } from './features/ai-economics/AiEconomicsCalculatorPage';
import { SpatialExperiencePage } from './features/world-model/SpatialExperiencePage';
import { WorldModelDiagramPage } from './features/world-model/WorldModelDiagramPage';
import { WorldModelConsultantPage } from './features/consultant/WorldModelConsultantPage';
import { PossibleWorldsPage } from './features/possible-worlds/PossibleWorldsPage';
import './features/possible-worlds/possibleWorlds.css';
import backSvg from './back.svg';
import LoadingScreen from './LoadingScreen';
import LoadingPrivacyControls from './features/privacy/LoadingPrivacyControls';
import PrivacyDataPage from './features/privacy/PrivacyDataPage';

function StandalonePage({ children }) {
  return <>{children}<div style={{position: 'fixed', right: 16, bottom: 16, zIndex: 10000}}><LoadingPrivacyControls /></div></>;
}
import { INITIAL_FRAME_COUNT } from './features/root/InitialFrameSignal';
import { appBasePath } from './utils/paths';

const FREEZE_LOADING_SCREEN = false;
const ROOT_LOAD_STARTED_AT = typeof performance !== "undefined" ? performance.now() : Date.now();

function Root() {
  const [, params] = useRoute('/neo/:id');
  const [isSpatialExperience, spatialParams] = useRoute('/space/:entitySet');
  const [isWorldModelDiagram] = useRoute('/world-model');
  const [isPossibleWorlds] = useRoute('/possible-worlds');
  const [isWorldModelConsultant] = useRoute('/world-model-consultant');
  const [isConsultantShortRoute] = useRoute('/consultant');
  const [isAiEconomicsCalculators] = useRoute('/ai-economics-calculators');
  const [, setLocation] = useLocation();
  const {
    active: assetsLoading,
    loaded: assetsLoaded,
    total: assetsTotal,
    progress: assetProgress,
    errors: assetErrors,
  } = useProgress();

  // ✅ Escape key = back in history (or / if no history)
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        setLocation('/');
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    window.setLocation = setLocation;
    window.navigate = setLocation;

    return () => {
      delete window.setLocation;
      delete window.navigate;
    };
  }, [setLocation]);

  const [loading, setLoading] = useState(true);
  const [canvasReady, setCanvasReady] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [sceneMountProgress, setSceneMountProgress] = useState(0);
  const [sceneRenderFrames, setSceneRenderFrames] = useState(0);
  const [highestAssetProgress, setHighestAssetProgress] = useState(0);
  const completedAssetTasks = Math.min(
    assetsTotal,
    Math.max(0, assetsLoaded + assetErrors.length)
  );
  const completedRenderTasks = Math.min(INITIAL_FRAME_COUNT, sceneRenderFrames);
  const totalBootTasks = assetsTotal + INITIAL_FRAME_COUNT;
  const completedBootTasks = completedAssetTasks + completedRenderTasks;
  const measuredAssetProgress =
    assetsTotal > 0 ? Math.round((completedAssetTasks / assetsTotal) * 100) : 0;
  const assetProgressPercent = highestAssetProgress;
  const renderProgressPercent = Math.round(
    (completedRenderTasks / INITIAL_FRAME_COUNT) * 100
  );

  useEffect(() => {
    if (assetsTotal <= 0) return;

    setHighestAssetProgress((highest) => Math.max(highest, measuredAssetProgress));
  }, [assetsTotal, measuredAssetProgress]);

  useEffect(() => {
    const asyncLoadCanvas = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      setCanvasReady(true);
    };

    asyncLoadCanvas();
  }, []);

  useEffect(() => {
    const assetsReady =
      assetsTotal > 0 &&
      ((!assetsLoading && assetProgress >= 100) || assetErrors.length > 0);
    if (!loading || !canvasReady || !assetsReady) {
      return undefined;
    }

    setLoading(false);
    return undefined;
  }, [assetErrors, assetProgress, assetsLoading, assetsTotal, canvasReady, loading]);

  useEffect(() => {
    if (loading || typeof window === "undefined") return undefined;

    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    window.__worldModelLoadMetrics = {
      loadMs: Math.max(0, Math.round(now - ROOT_LOAD_STARTED_AT)),
      canvasReady,
      assetsLoaded,
      assetsTotal,
      sceneReady,
      recordedAt: new Date().toISOString(),
    };
    return undefined;
  }, [assetsLoaded, assetsTotal, canvasReady, loading, sceneReady]);

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    const failSafeTimer = window.setTimeout(() => {
      setLoading(false);
    }, 7000);

    return () => window.clearTimeout(failSafeTimer);
  }, [loading]);

  const isJozLlmDashboard = window.location.pathname
    .replace(/\/+$/, '')
    .endsWith('/joz-llm-dashboard');

  if (isWorldModelConsultant || isConsultantShortRoute) {
    return <StandalonePage><WorldModelConsultantPage /></StandalonePage>;
  }

  if (isPossibleWorlds) {
    return <StandalonePage><PossibleWorldsPage /></StandalonePage>;
  }

  if (isJozLlmDashboard) {
    return <JozLlmDashboardPage />;
  }

  if (isAiEconomicsCalculators) {
    return <StandalonePage><AiEconomicsCalculatorPage /></StandalonePage>;
  }

  if (isSpatialExperience) {
    return <StandalonePage><SpatialExperiencePage entitySet={spatialParams?.entitySet} /></StandalonePage>;
  }

  if (isWorldModelDiagram) {
    return <StandalonePage><WorldModelDiagramPage /></StandalonePage>;
  }

  return (
    <>
      <LoadingScreen
        assetProgress={assetProgressPercent}
        mountProgress={sceneMountProgress}
        renderProgress={renderProgressPercent}
        visible={loading || !sceneReady || FREEZE_LOADING_SCREEN}
        settled={!loading && sceneReady && !FREEZE_LOADING_SCREEN}
      />
      {canvasReady && (
        <div
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        >
          <App
            isInitialLoading={loading}
            onSceneMount={setSceneMountProgress}
            onSceneRenderProgress={setSceneRenderFrames}
            onSceneReady={() => setSceneReady(true)}
          />

          {/* 🧭 Back button */}
          <button
            className="back-nav-button"
            style={{ visibility: params ? 'visible' : 'hidden' }}
            onClick={() => setLocation('/')}
          >
            <img src={backSvg} alt="Back" />
          </button>
        </div>
      )}
    </>
  );
}

function Entry() {
  const [isDataControls] = useRoute('/privacy/data');
  return isDataControls ? <PrivacyDataPage /> : <Root />;
}

createRoot(document.getElementById('root')).render(
  <Router base={appBasePath}>
    <Entry />
  </Router>
);
